import Speech from "speak-tts";

let speech;
let isLipSyncInitialized = false;
// We will manage a global AudioContext to ensure it can be resumed.
let audioContext;

// This maps common mouth shape visemes to the Oculus standard
const visemeMap = {
  // English Vowels
  a: "viseme_aa",
  e: "viseme_E",
  i: "viseme_I",
  o: "viseme_O",
  u: "viseme_U",
  // Consonants by shape group
  B: "viseme_PP", // b, m, p
  C: "viseme_DD", // c, d, g, k, n, s, t, z
  F: "viseme_FF", // f, v
  E: "viseme_TH", // th
  S: "viseme_SS", // sh, ch
  R: "viseme_RR", // r
};

/**
 * Initializes the speak-tts engine.
 */
export const initializeLipSync = () => {
  if (isLipSyncInitialized) return Promise.resolve();

  return new Promise((resolve, reject) => {
    // Crucially, create the AudioContext here if it doesn't exist.
    if (!audioContext) {
      try {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
      } catch (e) {
        console.error("Web Audio API is not supported in this browser.");
        return reject(e);
      }
    }

    speech = new Speech();
    speech
      .init({
        volume: 0.8,
        lang: "en-US",
        rate: 1,
        pitch: 1,
      })
      .then((data) => {
        isLipSyncInitialized = true;
        console.log("speak-tts voice engine initialized.", data);
        resolve();
      })
      .catch((e) => {
        console.error("An error occurred while initializing speak-tts:", e);
        reject(e);
      });
  });
};

/**
 * A robust method to unlock the browser's audio context after a user gesture.
 * This is the most reliable cross-browser method.
 */
export const unlockAudio = () => {
  console.log("Attempting to unlock audio context...");
  if (!audioContext) {
    console.error("AudioContext not initialized.");
    return Promise.reject("AudioContext not initialized.");
  }

  if (audioContext.state === "suspended") {
    return audioContext
      .resume()
      .then(() => {
        console.log("AudioContext resumed successfully.");
      })
      .catch((err) => {
        console.error("Error resuming AudioContext:", err);
        throw err; // Propagate the error
      });
  }

  // If it's already running or closed, we can resolve immediately.
  console.log("AudioContext is already in a usable state:", audioContext.state);
  return Promise.resolve();
};

/**
 * Enables speech synthesis by requesting user permission.
 * This is often the first "user gesture" that helps unlock audio.
 */
export const enableSpeech = () => {
  return new Promise((resolve, reject) => {
    if (!isLipSyncInitialized) {
      reject(new Error("Speech engine not initialized"));
      return;
    }

    // Test speech to trigger permission request
    speech
      .speak({
        text: "Speech enabled.",
        queue: false,
        listeners: {
          onstart: () => {
            console.log("Speech permission granted and test speech started.");
            resolve();
          },
          onend: () => {
            console.log("Test speech finished.");
          },
          onerror: (e) => {
            console.error("Speech permission error during test speak:", e);
            reject(e);
          },
        },
      })
      .catch((e) => {
        console.error("Failed to enable speech with test speak:", e);
        reject(e);
      });
  });
};

/**
 * Generates viseme animation and speaks text, mutating a ref.
 */
export const speakWithLipSync = async (
  text,
  animationQueueRef,
  onStart,
  onEnd
) => {
  if (!isLipSyncInitialized) {
    console.error("speak-tts is not initialized.");
    onEnd();
    return;
  }

  // Before speaking, ensure the context is running.
  if (audioContext && audioContext.state !== "running") {
    console.warn(
      "Audio context is not running. Speech may be silent. Attempting to resume again."
    );
    await audioContext
      .resume()
      .catch((e) => console.error("Could not resume audio before speak", e));
  }

  console.log("Speaking with lip sync:", text.substring(0, 50) + "...");

  speech
    .speak({
      text: text,
      queue: false,
      listeners: {
        onboundary: (event) => {
          const spokenText = event.target.text;
          const remainingText = spokenText.substring(event.charIndex);
          const currentWord = remainingText.split(" ")[0] || "";

          const queue = [];
          for (const char of currentWord) {
            const lowerChar = char.toLowerCase();
            let visemeType = "C";
            if ("aeiou".includes(lowerChar)) visemeType = lowerChar;
            else if ("bmp".includes(lowerChar)) visemeType = "B";
            else if ("fv".includes(lowerChar)) visemeType = "F";

            const viseme = visemeMap[visemeType] || "viseme_sil";
            queue.push({ viseme, duration: 0.075 });
          }
          queue.push({ viseme: "viseme_sil", duration: 0.1 });

          // Replace the entire queue instead of appending - this was the key difference
          if (animationQueueRef.current) {
            animationQueueRef.current = queue;
          }
        },
        onstart: () => {
          console.log("Speech started");
          onStart();
          if (animationQueueRef.current) {
            animationQueueRef.current = [];
          }
        },
        onend: () => {
          console.log("Speech ended");
          onEnd();
          if (animationQueueRef.current) {
            animationQueueRef.current = [];
          }
        },
        onerror: (e) => {
          console.error("Speech error:", e);
          onEnd();
          if (animationQueueRef.current) {
            animationQueueRef.current = [];
          }
        },
      },
    })
    .catch((e) => {
      console.error("An error occurred while trying to speak:", e);
      onEnd();
    });
};

export const cancelSpeech = () => {
  if (speech && speech.speaking()) {
    speech.cancel();
  }
};
